import {
  createMaintenanceTemplate,
  listPuckPages,
} from "@/api/cms-puck/api";
import {
  useGeneralSettingsFormData,
  useUpdateGeneralSettings,
} from "@/api/cms";
import CmsMaintenanceImageUpload from "@/components/cms/CmsMaintenanceImageUpload";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Textarea } from "@/components/ui/textarea";
import { useToast } from "@/hooks/use-toast";
import { useAuth } from "@/context/auth/AuthContext";
import { useCmsWebsiteLanguages } from "@/hooks/use-cms-website-languages";
import type { SupportedLocale } from "@/i18n";
import { CmsAdminShell } from "@/pages/cms/advanced/AdminShell";
import { useCmsNew } from "@/lib/cms/cms-new-i18n";
import { cmsPhrase, puckPageStatusLabel } from "@/lib/cms/cms-new-phrases";
import type { GeneralSettings } from "@/lib/cms/cms-types";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ExternalLink, Plus } from "lucide-react";
import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";

const PAGES_KEY = ["cms-puck", "pages"] as const;
const SETTINGS_KEY = ["cms", "general-settings"] as const;
const NONE_VALUE = "__none__";

type MaintenanceMode = "SIMPLE" | "PUCK";

interface LocaleContent {
  title: string;
  line1: string;
  line2: string;
}
type MaintenanceContentState = Record<string, LocaleContent>;

const LANGUAGE_LABELS: Record<string, string> = {
  fr: "Français",
  en: "English",
  es: "Español",
  nl: "Nederlands",
};

function emptyLocaleContent(): LocaleContent {
  return { title: "", line1: "", line2: "" };
}

/** Normalise le contenu venant de l'API en un état éditable par langue active. */
function buildContentState(
  raw: GeneralSettings["maintenanceContent"] | undefined,
  languages: SupportedLocale[],
): MaintenanceContentState {
  const state: MaintenanceContentState = {};
  for (const lang of languages) {
    const entry = raw?.[lang];
    state[lang] = {
      title: entry?.title ?? "",
      line1: entry?.line1 ?? "",
      line2: entry?.line2 ?? "",
    };
  }
  return state;
}

export default function CMSAdvancedMaintenance() {
  const cmsNew = useCmsNew();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const { toast } = useToast();
  const { instanceData } = useAuth();
  const { websiteLanguages, defaultWebsiteLanguage } = useCmsWebsiteLanguages();

  const { data: generalSettings, isLoading: settingsLoading } =
    useGeneralSettingsFormData();
  const pagesQuery = useQuery({
    // Clé distincte de la liste Pages (complète) pour ne pas partager le cache.
    queryKey: [
      ...PAGES_KEY,
      {
        excludeCatalogFlow: true,
        excludeMemberFlow: true,
        excludeShellFlow: true,
        excludeSearchFlow: true,
        excludeAbonnementPage: true,
      },
    ],
    queryFn: () =>
      listPuckPages({
        excludeCatalogFlow: true,
        excludeMemberFlow: true,
        excludeShellFlow: true,
        excludeSearchFlow: true,
        excludeAbonnementPage: true,
      }),
  });

  const updateSettings = useUpdateGeneralSettings();

  const createTemplateMutation = useMutation({
    mutationFn: createMaintenanceTemplate,
    onSuccess: ({ page }) => {
      void queryClient.invalidateQueries({ queryKey: PAGES_KEY });
      void queryClient.invalidateQueries({ queryKey: SETTINGS_KEY });
      toast({
        title: cmsNew("cms.new.auto.title.page_de_maintenance_creee", "Page de maintenance créée"),
        variant: "success",
        duration: 1600,
      });
      navigate(`/cms/advanced/${page.id}`);
    },
    onError: (error: unknown) => {
      const message =
        error instanceof Error
          ? error.message
          : cmsPhrase(cmsNew, "createMaintenancePageFailed");
      toast({ title: message, variant: "error" });
    },
  });

  const maintenanceEnabled = generalSettings?.maintenanceEnabled ?? false;
  const maintenanceMode: MaintenanceMode =
    (generalSettings?.maintenanceMode as MaintenanceMode) ?? "SIMPLE";
  const maintenancePageId = generalSettings?.maintenancePageId ?? null;
  const cmsId = generalSettings?.id ?? instanceData?.CMS?.id;

  // État local éditable du contenu simple (par langue).
  const [content, setContent] = useState<MaintenanceContentState>({});
  // Ne resynchronise l'état local que si le contenu SERVEUR change réellement,
  // pour ne pas écraser la saisie en cours lors des refetch déclenchés par
  // l'upload d'image ou la (dé)publication.
  const lastSyncedRef = useRef<string | null>(null);
  useEffect(() => {
    if (!generalSettings) return;
    const serverContent = buildContentState(
      generalSettings.maintenanceContent,
      websiteLanguages,
    );
    const serialized = JSON.stringify(serverContent);
    if (serialized !== lastSyncedRef.current) {
      lastSyncedRef.current = serialized;
      setContent(serverContent);
    }
  }, [generalSettings, websiteLanguages]);

  const assigned = pagesQuery.data?.find((p) => p.id === maintenancePageId);
  const isDraft = assigned && assigned.status !== "PUBLISHED";

  // Le contenu simple est-il suffisant pour publier ? (titre dans la langue par défaut)
  const hasSimpleContent = useMemo(() => {
    const primary = content[defaultWebsiteLanguage];
    return Boolean(primary?.title?.trim());
  }, [content, defaultWebsiteLanguage]);

  const canPublish =
    maintenanceMode === "SIMPLE" ? hasSimpleContent : Boolean(maintenancePageId);

  const contentDirty = useMemo(() => {
    if (!generalSettings) return false;
    const server = buildContentState(
      generalSettings.maintenanceContent,
      websiteLanguages,
    );
    return JSON.stringify(server) !== JSON.stringify(content);
  }, [generalSettings, websiteLanguages, content]);

  const saveSettings = (patch: {
    maintenanceEnabled?: boolean;
    maintenanceMode?: MaintenanceMode;
    maintenanceContent?: MaintenanceContentState;
    maintenancePageId?: string | null;
  }) => {
    if (!cmsId) {
      toast({
        title: cmsNew("cms.new.auto.title.impossible_denregistrer_cms_introuvable", "Impossible d'enregistrer : CMS introuvable"),
        variant: "error",
      });
      return;
    }
    updateSettings.mutate(
      {
        id: cmsId,
        headerAlwaysVisible: generalSettings?.headerAlwaysVisible ?? false,
        headerAlwaysVisibleMobile:
          generalSettings?.headerAlwaysVisibleMobile ?? false,
        socials: generalSettings?.socials ?? [],
        isMixedCRMode: generalSettings?.isMixedCRMode ?? false,
        defaultWebsiteLanguage:
          generalSettings?.defaultWebsiteLanguage ?? "fr",
        websiteLanguages: generalSettings?.websiteLanguages ?? ["fr"],
        playerAutoplay: generalSettings?.playerAutoplay ?? false,
        playerControls: generalSettings?.playerControls ?? true,
        playerMuted: generalSettings?.playerMuted ?? false,
        playerLoop: generalSettings?.playerLoop ?? false,
        maintenanceEnabled,
        maintenanceMode,
        maintenancePageId,
        abonnementPageId: generalSettings?.abonnementPageId ?? null,
        ...patch,
      },
      {
        onSuccess: () => {
          void queryClient.invalidateQueries({ queryKey: SETTINGS_KEY });
          toast({
            title: cmsNew("cms.new.auto.title.reglages_maintenance_enregistres", "Réglages maintenance enregistrés"),
            variant: "success",
            duration: 1600,
          });
        },
        onError: () => {
          toast({
            title: cmsNew("cms.new.auto.title.impossible_denregistrer", "Impossible d'enregistrer"),
            variant: "error",
          });
        },
      },
    );
  };

  const updateLocaleField = (
    lang: string,
    field: keyof LocaleContent,
    value: string,
  ) => {
    setContent((prev) => ({
      ...prev,
      [lang]: { ...(prev[lang] ?? emptyLocaleContent()), [field]: value },
    }));
  };

  const languageLabel = (lang: string) =>
    LANGUAGE_LABELS[lang] ?? lang.toUpperCase();

  return (
    <CmsAdminShell
      title={cmsNew("cms.new.auto.title.maintenance", "Maintenance")}
      subtitle={cmsNew("cms.new.maintenance.subtitle", "Affichez une page de maintenance à tous les visiteurs (sans en-tête ni pied de page).")}
    >
      <div className="space-y-6 px-6 py-4">
        <p className="text-sm text-slate-600">
          {cmsNew("cms.new.maintenance.redirectHintBefore", "Lorsque la maintenance est activée, le site public redirige vers")}{" "}
          <code className="text-xs">/maintenance</code>
          {cmsNew("cms.new.maintenance.redirectHintAfter", ". Les prévisualisations depuis l'éditeur CMS avancé (bouton Aperçu) et les sessions backoffice contournent la redirection.")}
        </p>

        {settingsLoading ? (
          <p className="text-sm text-slate-500">{cmsNew("cms.new.auto.text.chargement", "Chargement…")}</p>
        ) : (
          <>
            {/* Toggle "Publier la page" */}
            <div className="flex items-center justify-between rounded-lg border border-slate-200 bg-white p-4">
              <div className="space-y-1">
                <Label htmlFor="maintenance-enabled" className="text-sm font-medium">
                  {cmsNew("cms.new.maintenance.publishLabel", "Publier la page")}
                </Label>
                <p className="text-xs text-slate-500">
                  {cmsNew("cms.new.maintenance.toggleHint", "Active la redirection vers la page de maintenance.")}
                </p>
              </div>
              <Switch
                id="maintenance-enabled"
                checked={maintenanceEnabled}
                disabled={updateSettings.isPending || (!maintenanceEnabled && !canPublish)}
                onCheckedChange={(checked) =>
                  saveSettings({ maintenanceEnabled: checked })
                }
              />
            </div>

            {!canPublish ? (
              <p className="text-sm text-amber-700">
                {maintenanceMode === "SIMPLE"
                  ? cmsNew("cms.new.maintenance.noSimpleContentHint", "Renseignez au moins un titre dans la langue principale pour pouvoir publier.")
                  : cmsNew("cms.new.maintenance.noPageHint", "Créez ou assignez une page de maintenance pour pouvoir activer le mode.")}
              </p>
            ) : null}

            {/* Sélecteur de mode */}
            <Tabs
              value={maintenanceMode}
              onValueChange={(next) =>
                saveSettings({ maintenanceMode: next as MaintenanceMode })
              }
            >
              <TabsList>
                <TabsTrigger value="SIMPLE" disabled={updateSettings.isPending}>
                  {cmsNew("cms.new.maintenance.modeSimple", "Page simple")}
                </TabsTrigger>
                <TabsTrigger value="PUCK" disabled={updateSettings.isPending}>
                  {cmsNew("cms.new.maintenance.modeAdvanced", "Page avancée (CMS)")}
                </TabsTrigger>
              </TabsList>

              {/* Mode SIMPLE : titre + 2 lignes par langue + 1 image */}
              <TabsContent value="SIMPLE" className="space-y-4 pt-4">
                <div className="space-y-2 rounded-lg border border-slate-200 bg-white p-4">
                  <Label className="text-sm font-medium text-slate-800">
                    {cmsNew("cms.new.maintenance.imageLabel", "Image")}
                  </Label>
                  <p className="text-xs text-slate-500">
                    {cmsNew("cms.new.maintenance.imageHint", "Une seule image, partagée par toutes les langues, affichée centrée.")}
                  </p>
                  <CmsMaintenanceImageUpload />
                </div>

                <div className="space-y-3 rounded-lg border border-slate-200 bg-white p-4">
                  <Label className="text-sm font-medium text-slate-800">
                    {cmsNew("cms.new.maintenance.textsLabel", "Textes par langue")}
                  </Label>

                  <Tabs defaultValue={defaultWebsiteLanguage}>
                    <TabsList>
                      {websiteLanguages.map((lang) => (
                        <TabsTrigger key={lang} value={lang}>
                          {languageLabel(lang)}
                        </TabsTrigger>
                      ))}
                    </TabsList>

                    {websiteLanguages.map((lang) => {
                      const entry = content[lang] ?? emptyLocaleContent();
                      return (
                        <TabsContent
                          key={lang}
                          value={lang}
                          className="space-y-3 pt-3"
                        >
                          <div className="space-y-1">
                            <Label htmlFor={`m-title-${lang}`} className="text-xs text-slate-600">
                              {cmsNew("cms.new.maintenance.fieldTitle", "Titre")}
                            </Label>
                            <Input
                              id={`m-title-${lang}`}
                              value={entry.title}
                              onChange={(e) =>
                                updateLocaleField(lang, "title", e.target.value)
                              }
                              placeholder={cmsNew("cms.new.maintenance.fieldTitlePlaceholder", "Site en maintenance")}
                            />
                          </div>
                          <div className="space-y-1">
                            <Label htmlFor={`m-line1-${lang}`} className="text-xs text-slate-600">
                              {cmsNew("cms.new.maintenance.fieldLine1", "Ligne de texte 1")}
                            </Label>
                            <Textarea
                              id={`m-line1-${lang}`}
                              value={entry.line1}
                              rows={2}
                              onChange={(e) =>
                                updateLocaleField(lang, "line1", e.target.value)
                              }
                            />
                          </div>
                          <div className="space-y-1">
                            <Label htmlFor={`m-line2-${lang}`} className="text-xs text-slate-600">
                              {cmsNew("cms.new.maintenance.fieldLine2", "Ligne de texte 2")}
                            </Label>
                            <Textarea
                              id={`m-line2-${lang}`}
                              value={entry.line2}
                              rows={2}
                              onChange={(e) =>
                                updateLocaleField(lang, "line2", e.target.value)
                              }
                            />
                          </div>
                        </TabsContent>
                      );
                    })}
                  </Tabs>

                  <div className="flex justify-end pt-1">
                    <Button
                      type="button"
                      size="sm"
                      disabled={updateSettings.isPending || !contentDirty}
                      onClick={() => saveSettings({ maintenanceContent: content })}
                    >
                      {cmsNew("cms.new.auto.text.enregistrer", "Enregistrer")}
                    </Button>
                  </div>
                </div>
              </TabsContent>

              {/* Mode PUCK : page CMS avancé dédiée */}
              <TabsContent value="PUCK" className="space-y-2 pt-4">
                <div className="space-y-2 rounded-lg border border-slate-200 bg-white p-4">
                  <Label className="text-sm font-medium text-slate-800">
                    {cmsPhrase(cmsNew, "maintenancePageLabel")}
                  </Label>
                  <Select
                    value={maintenancePageId ?? NONE_VALUE}
                    onValueChange={(next) =>
                      saveSettings({
                        maintenancePageId: next === NONE_VALUE ? null : next,
                      })
                    }
                    disabled={updateSettings.isPending || pagesQuery.isLoading}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder={cmsNew("cms.new.auto.placeholder.aucune_page", "Aucune page")} />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value={NONE_VALUE}>{cmsNew("cms.new.auto.text.aucune_page", "Aucune page")}</SelectItem>
                      {(pagesQuery.data ?? []).map((page) => (
                        <SelectItem key={page.id} value={page.id}>
                          {page.name}
                          {page.status !== "PUBLISHED" ? ` (${puckPageStatusLabel(cmsNew, page.status)})` : ""}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>

                  {isDraft ? (
                    <p className="text-xs text-amber-700">
                      {cmsPhrase(cmsNew, "draftPublishHint")}
                    </p>
                  ) : null}

                  <div className="flex flex-wrap gap-2 pt-1">
                    {maintenancePageId ? (
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        className="h-8 gap-1"
                        onClick={() => navigate(`/cms/advanced/${maintenancePageId}`)}
                      >
                        <ExternalLink size={14} />
                        {cmsPhrase(cmsNew, "editPage")}
                      </Button>
                    ) : (
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        className="h-8 gap-1"
                        disabled={createTemplateMutation.isPending}
                        onClick={() => createTemplateMutation.mutate()}
                      >
                        <Plus size={14} />
                        {createTemplateMutation.isPending
                          ? cmsPhrase(cmsNew, "creating")
                          : cmsPhrase(cmsNew, "createTemplate")}
                      </Button>
                    )}
                  </div>
                </div>
              </TabsContent>
            </Tabs>
          </>
        )}
      </div>
    </CmsAdminShell>
  );
}
